import { apiService } from "@/services/apiService";
import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import type { HttpResponse } from "@repo/shared/api";
import type {
  AgendaItemMetadataConfig,
  AppConfig,
  DocumentUploadTypesConfig,
  ExampleFilesConfig,
  MeetingTypesConfig,
  MotionTypesConfig,
  OrganizationTypesConfig,
  TermsAndConditionsConfig,
} from "@repo/types";
import type { Observable } from "rxjs";
import { cacheDefaults } from "./common";
import { baseRepo } from "./baseRepo";

export type ConfigType =
  | "config"
  | "organizationTypes"
  | "meetingTypes"
  | "documentUploadTypes"
  | "agendaItemMetadata"
  | "termsAndConditions"
  | "motionTypes"
  | "exampleFiles";

const typeToMethod: Record<ConfigType, keyof typeof apiService.v1> = {
  config: "getAppConfig",
  organizationTypes: "getOrganizationTypesConfig",
  meetingTypes: "getMeetingTypeConfig",
  documentUploadTypes: "getDocumentUploadTypesConfig",
  agendaItemMetadata: "getAgendaItemMetadataConfig",
  termsAndConditions: "getTermsAndConditionsConfig",
  motionTypes: "getMotionTypesConfig",
  exampleFiles: "getExampleFileConfig",
};
Object.setPrototypeOf(typeToMethod, null);

export type ConfigTypeTableBase = {
  config: AppConfig;
  organizationTypes: OrganizationTypesConfig;
  meetingTypes: MeetingTypesConfig;
  documentUploadTypes: DocumentUploadTypesConfig;
  agendaItemMetadata: AgendaItemMetadataConfig;
  termsAndConditions: TermsAndConditionsConfig;
  motionTypes: MotionTypesConfig;
  exampleFiles: ExampleFilesConfig;
};

export type ConfigTypeTable = {
  [K in keyof ConfigTypeTableBase]: ConfigTypeTableBase[K] & { id: K };
};

export type AnyConfig = ConfigTypeTable[ConfigType];

type ConfigGetKey = { type: "get"; configId: ConfigType };
type ConfigKey = ConfigGetKey;

export class ConfigRepository {
  private cache: RepoCache<ConfigType, AnyConfig, ObjectKeyHasher, AnyClock>;

  publishNewer(config: AnyConfig) {
    this.cache.publish(config.id, config);
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: false,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as ConfigKey;

        switch (key.type) {
          case "get": {
            const { configId } = key;
            const config =
              configId in typeToMethod
                ? (
                    await (
                      apiService.v1[typeToMethod[configId]] as () => Promise<
                        HttpResponse<ConfigTypeTable[ConfigType], unknown>
                      >
                    )()
                  ).data
                : null;
            publish(configId, config);
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as ConfigKey;

        switch (key.type) {
          case "get": {
            return table.get(key.configId) ?? null;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { id } = entryData;
        // affect
        affectsKey(this.getKey(id));
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(configId: ConfigType): ConfigGetKey {
    return { type: "get", configId };
  }

  get<T extends ConfigType>(
    type: T,
    options?: GetObservableOptions
  ): Observable<ConfigTypeTable[T]> {
    const key = this.getKey(type);
    return this.cache.observable(key, options);
  }
  getCached<T extends ConfigType>(type: T): ConfigTypeTable[T] | null {
    const key = this.getKey(type);
    return this.cache.collectIfUnexpired(key, null);
  }

  invalidate(type: ConfigType) {
    this.cache.invalidateEntry(type);
  }

  invalidateAll() {
    this.cache.clear();
  }
}

export const configRepo = new ConfigRepository();
